import {
  useNavigation,
  useFocusEffect,
  useIsFocused,
} from "@react-navigation/native";
import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  View,
  TouchableOpacity,
} from "react-native";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import colors from "@constants/color";
import t from "@constants/localization";
import CxCard from "@screens/MyDeliveries/components/CxCard";
import { getCrateDeliveryDetails, getDailyRTOSV3 } from "@services/api";
import useSarvCall from "@hooks/useSarvCall";
import SarvCallModal from "@screens/MyTasks/components/SarvCallModal.tsx";
import { getDayMonth } from "@utils/getFormattedDate";
import CustomText from "@components/CustomText";
import debounce from "lodash/debounce";
import InfoTooltipModal from "@screens/MyDeliveries/components/InfoTooltipModal";
import ChevronRight from "@assets/svg/ChevronRight";
import HeaderWithSearch from "./HeaderWithSearch";

const PATH = "screens/MyDeliveries/DeliveryOrdersList";

const DeliveryOrdersList = ({ route }) => {
  const navigation = useNavigation();
  const [ordersData, setOrdersData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [usersData, setUsersData] = useState([]);
  const isFocused = useIsFocused();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { forNotDelivered = false } = route?.params || {};
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [infoTooltipData, setInfoTooltipData] = useState({});
  const { createCall, showSarvModal, setShowSarvModal, updateSarvData } =
    useSarvCall();

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]),
  );

  const fetchData = async () => {
    try {
      if (forNotDelivered) {
        const { data } = await getDailyRTOSV3({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
        });
        setOrdersData(data);
        updateSarvData(data);
      } else {
        const { data } = await getCrateDeliveryDetails({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
          type: "ASSIGNED",
        });
        setOrdersData(data);
        setUsersData(data?.usersTodaysDeliveries);
        updateSarvData(data);
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
    setIsRefreshing(false);
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const sortUpdates = useMemo(
    () =>
      function () {
        return ordersData.usersTodaysDeliveries;
      },
    [ordersData],
  );

  const checkOrderDatailsEmpty = () => {
    return !(ordersData && ordersData?.usersTodaysDeliveries?.length > 0);
  };

  const renderItem = ({ item }) => {
    return (
      <CxCard
        item={item}
        date={ordersData?.date}
        spokeName={route?.params?.spokeName}
        setInfoTooltipData={setInfoTooltipData}
        setInfoTooltipVisible={setInfoTooltipVisible}
        type={route?.params?.type}
        createCall={createCall}
      />
    );
  };

  const keyExtractor = (item, index) => `${item.user_id}-${index}`;

  const getProgressWidth = () => {
    if (ordersData) {
      return (ordersData.submittedUsers * 100) / ordersData.totalUsers;
    }
    return 0;
  };

  const onPressCrossIcon = () => onSearchInputChange("");

  const onSearchInputChange = debounce((text) => {
    const filteredData = {
      ...ordersData,
      usersTodaysDeliveries: usersData.filter(
        (v) =>
          containText(text, v?.user_name) ||
          containText(text, v?.user_phone) ||
          containText(text, v?.cx_formatted_address),
      ),
    };
    setOrdersData(filteredData);
  }, 500);

  const containText = (text, item) => {
    const lowerCasedText = text?.trim().toLowerCase();
    return item?.toLowerCase().includes(lowerCasedText);
  };

  const seeAllSamasya = () => {
    navigation.navigate("AllSamasya", {
      spokeName: route?.params?.spokeName,
      date: route?.params?.date,
    });
  };

  const ProgressBar = () => (
    <View style={styles.progressBarContainer}>
      <View style={styles.pbHeader}>
        <CustomText
          variant="body_text_4"
          style={{ color: "rgba(0, 0, 0, 0.56)" }}
        >
          {"कस्टमर्स के आइटम्स तैयार "}
        </CustomText>
        <View style={styles.pbUser}>
          <CustomText
            variant="title_16"
            style={{
              color: ordersData.submittedUsers
                ? "#00AB59"
                : "rgba(0, 0, 0, 0.56)",
            }}
          >
            {ordersData.submittedUsers}
          </CustomText>
          <CustomText
            variant="title_16"
            style={{ color: "rgba(0, 0, 0, 0.56)" }}
          >
            {`/${ordersData.totalUsers}`}
          </CustomText>
        </View>
      </View>
      <View>
        <View style={styles.progressBar} />
        <View
          style={[
            styles.progressBar,
            {
              backgroundColor: "rgba(0, 171, 89, 1)",
              marginTop: -8,
              width: `${getProgressWidth()}%`,
            },
          ]}
        />
      </View>
    </View>
  );

  return (
    <View style={{ flex: 1 }}>
      <HeaderWithSearch
        headertitle={
          forNotDelivered
            ? t(`${PATH}/returnedOrders`)
            : t(`${PATH}/deliveryDetails`, {
                date: getDayMonth(route?.params?.date),
              })
        }
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        searchPlaceholder="कस्टमर के नाम, फ़ोन नंबर, एड्रेस से सर्च करें..."
        isSearchEnabled={true}
        bottomComponent={ordersData ? <ProgressBar /> : <></>}
        onPressCrossIcon={onPressCrossIcon}
        onSearchInputChange={onSearchInputChange}
      />
      <SarvCallModal
        showSarvModal={showSarvModal}
        setShowSarvModal={setShowSarvModal}
      />
      <InfoTooltipModal
        visible={infoTooltipVisible}
        setVisible={setInfoTooltipVisible}
        data={infoTooltipData}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : checkOrderDatailsEmpty() ? (
        <EmptySearchList />
      ) : (
        <>
          <FlatList
            keyExtractor={keyExtractor}
            contentContainerStyle={styles.containerStyle}
            data={sortUpdates()}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            onRefresh={onRefresh}
            refreshing={isRefreshing}
          />
          {ordersData?.is_any_item_disputed ? (
            <TouchableOpacity
              testID="docl-see-all-samasya"
              style={styles.samasyaContainer}
              onPress={() => seeAllSamasya()}
            >
              <CustomText variant="title_16" style={{ color: "white" }}>
                {ordersData?.disputed_return_text}
              </CustomText>
              <View style={styles.arrowContainer}>
                <ChevronRight
                  pathFill="rgba(0, 0, 0, 0.4)"
                  style={styles.arrow}
                />
              </View>
            </TouchableOpacity>
          ) : null}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  containerStyle: {
    paddingBottom: 8,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  progressBarContainer: {
    paddingHorizontal: 16,
    paddingBottom: 10,
  },
  pbHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  progressBar: {
    height: 8,
    backgroundColor: "rgba(0, 171, 89, 0.1)",
    borderRadius: 8,
  },
  pbUser: {
    flexDirection: "row",
  },
  arrowContainer: {
    backgroundColor: "white",
    borderRadius: 12,
    height: 24,
    width: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  arrow: { height: 16, width: 16 },
  samasyaContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#4D4D4D",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

export default DeliveryOrdersList;
